import { createFileRoute, redirect } from "@tanstack/react-router";
import { occFetch, occUrl } from "~/app/api/occ/occFetch";
import { openSavedConfiguration } from "~/domains/configurator/api/openSavedConfiguration";
import {
  configuratorPartnerHeaders,
  scopeConfiguratorResumeSourceKey,
} from "~/domains/configurator/configuratorPartnerContext";
import { currentResumeAuthEpoch, resumeAuthEpochUnchanged } from "~/domains/configurator/configuratorResumeAuthEpoch";
import { getConfiguratorResumeStore, productResumeSourceKey } from "~/domains/configurator/configuratorResumeStore";
import { markConfiguratorPreload } from "~/features/configurator/common/hooks/configuratorPreload";
import type { ConfiguratorInitResult } from "~/features/configurator/common/api/configuratorInitApi";
import { getConfiguratorPageRoute } from "~/features/configurator/common/configuratorRoutes";
import { loadConfiguratorV2Code } from "~/features/configurator-v2/common/data/configuratorV2Api";
import { adaptConfiguratorConfiguration } from "~/features/configurator-v2/common/data/adaptConfiguratorConfiguration";
import {
  adaptConfiguratorRuntimeContext,
  configuratorConfigurationKey,
  createConfiguratorInitialExpansion,
} from "~/features/configurator-v2/common/data/configuratorConfigurationQuery";
import { loadEmbeddedConfiguratorPageData } from "../embeddedProductConfigurator/$productCode";

export const Route = createFileRoute("/savedConfigurator/$code")({
  ssr: false,
  preload: false,
  validateSearch: (search: Record<string, unknown>): { token?: string } => ({
    token: typeof search.token === "string" ? search.token : undefined,
  }),
  loaderDeps: ({ search }) => ({
    token: search.token ?? null,
  }),
  loader: async ({ context, params, deps, abortController, preload }) => {
    if (preload || typeof window === "undefined") {
      return;
    }
    const assertCurrent = () => {
      if (abortController.signal.aborted || !resumeAuthEpochUnchanged(authEpoch)) {
        throw new Error("The page or session changed while loading the configuration.");
      }
    };
    const authEpoch = currentResumeAuthEpoch();
    const info = await openSavedConfiguration({ configuratorUUID: params.code, productCode: "", partnerToken: null });
    if (!info.productCode) {
      throw new Error("The saved configuration product is unavailable.");
    }
    const productCode = info.productCode;
    const partnerToken = deps.token;
    if (partnerToken !== null) {
      await loadEmbeddedConfiguratorPageData(context, productCode, partnerToken);
    }
    const resumeKey = await scopeConfiguratorResumeSourceKey(productResumeSourceKey(productCode), partnerToken);
    let configId: string;
    if (info.v2Product) {
      const response = await loadConfiguratorV2Code({ savedConfigurationCode: params.code }, partnerToken);
      assertCurrent();
      configId = response.data.configId;
      context.queryClient.setQueryData(configuratorConfigurationKey(productCode, partnerToken), {
        configuration: {
          ...adaptConfiguratorConfiguration(response.data),
          helpUrl: response.context.helpUrl?.trim() || null,
        },
        context: adaptConfiguratorRuntimeContext(response.context),
        initialExpansion: createConfiguratorInitialExpansion("load"),
      });
    } else {
      const response = await occFetch(occUrl("/cpq/v1/load-code"), {
        method: "POST",
        headers: {
          ...configuratorPartnerHeaders(partnerToken),
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ code: params.code }),
      });
      if (!response.ok) {
        throw new Error("The saved configuration could not be loaded.");
      }
      const envelope = (await response.json()) as ConfiguratorInitResult;
      assertCurrent();
      configId = envelope.data.configId;
      markConfiguratorPreload(envelope, partnerToken);
    }
    if (resumeKey) {
      getConfiguratorResumeStore().write(resumeKey, configId);
    }
    if (partnerToken !== null) {
      throw redirect({
        to: "/embeddedProductConfigurator/$productCode",
        params: { productCode },
        search: { token: partnerToken },
      });
    }
    throw redirect({ to: getConfiguratorPageRoute(productCode) });
  },
});
